/**
 * Cover Design Models
 * Book cover design configuration
 */

export interface CoverDesign {
  id: string
  bookId: string
  backgroundType: 'color' | 'gradient' | 'image' | 'pattern'
  backgroundColor?: string
  gradientColors?: [string, string]
  gradientDirection?: 'to-top' | 'to-bottom' | 'to-left' | 'to-right' | 'diagonal'
  backgroundImage?: string
  pattern?: 'dots' | 'lines' | 'grid' | 'waves'

  title: {
    text: string
    font: string
    fontSize: number
    color: string
    align: 'left' | 'center' | 'right'
    position: { x: number; y: number }
    shadow?: boolean
    bold?: boolean
    italic?: boolean
  }

  author: {
    text: string
    font: string
    fontSize: number
    color: string
    align: 'left' | 'center' | 'right'
    position: { x: number; y: number }
    shadow?: boolean
    bold?: boolean
    italic?: boolean
  }

  subtitle?: {
    text: string
    font: string
    fontSize: number
    color: string
    align: 'left' | 'center' | 'right'
    position: { x: number; y: number }
  }

  graphics?: Array<{
    id: string
    type: 'icon' | 'shape' | 'image'
    icon?: string
    imageUrl?: string
    shape?: 'circle' | 'rectangle' | 'line'
    color?: string
    position: { x: number; y: number }
    size: { width: number; height: number }
  }>

  dimensions: {
    width: number
    height: number
  }

  createdAt: Date
  updatedAt: Date
}

export const COVER_FONTS = [
  { id: 'georgia', name: 'Georgia', family: 'Georgia, serif' },
  { id: 'times', name: 'Times New Roman', family: 'Times New Roman, serif' },
  { id: 'palatino', name: 'Palatino', family: 'Palatino, serif' },
  { id: 'arial', name: 'Arial', family: 'Arial, sans-serif' },
  { id: 'helvetica', name: 'Helvetica', family: 'Helvetica, sans-serif' },
  { id: 'impact', name: 'Impact', family: 'Impact, sans-serif' },
  { id: 'courier', name: 'Courier', family: 'Courier, monospace' },
  { id: 'playfair', name: 'Playfair Display', family: 'serif' },
  { id: 'montserrat', name: 'Montserrat', family: 'sans-serif' },
]

export const COVER_GRADIENTS = [
  { id: 'sunset', colors: ['#FF6B6B', '#FFE66D'] as [string, string], name: 'Sunset' },
  { id: 'ocean', colors: ['#4E65FF', '#92EFFD'] as [string, string], name: 'Ocean' },
  { id: 'forest', colors: ['#134E5E', '#71B280'] as [string, string], name: 'Forest' },
  { id: 'purple', colors: ['#667EEA', '#F093FB'] as [string, string], name: 'Purple Dream' },
  { id: 'fire', colors: ['#F12711', '#F5AF19'] as [string, string], name: 'Fire' },
  { id: 'night', colors: ['#0F2027', '#2C5364'] as [string, string], name: 'Night Sky' },
  { id: 'rose', colors: ['#ED4264', '#FFEDBC'] as [string, string], name: 'Rose Gold' },
  { id: 'mint', colors: ['#00B4DB', '#0083B0'] as [string, string], name: 'Mint' },
]

export const DEFAULT_COVER_DESIGN: Omit<CoverDesign, 'id' | 'bookId' | 'createdAt' | 'updatedAt'> = {
  backgroundType: 'gradient',
  gradientColors: ['#667EEA', '#F093FB'],
  gradientDirection: 'diagonal',

  title: {
    text: '',
    font: 'Georgia, serif',
    fontSize: 48,
    color: '#FFFFFF',
    align: 'center',
    position: { x: 50, y: 35 },
    shadow: true,
    bold: true,
  },

  author: {
    text: '',
    font: 'Georgia, serif',
    fontSize: 24,
    color: '#FFFFFF',
    align: 'center',
    position: { x: 50, y: 60 },
    shadow: false,
    italic: true,
  },

  dimensions: {
    width: 600,
    height: 900,
  },
}
